import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import { downloadXlsx } from "../utils/excelSheet";
import Tooltip from "@mui/material/Tooltip";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import XlsxIcon from "../svg/XlsxIcon";
import CsvIcon from "../svg/CsvIcon";

const getCellValue = (row, column) => {
  const params = {
    row,
    field: column.field,
    value: row?.[column.field],
  };
  let value = column.valueGetter ? column.valueGetter(params) : params.value;

  if (column.valueFormatter) {
    value = column.valueFormatter({ ...params, value });
  }

  if (value === null || value === undefined) {
    return "";
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  return value;
};

const isPlainNumber = (value) => /^-?\d{1,3}(?:[.\s]\d{3})*(?:[.,]\d+)?$|^-?\d+(?:[.,]\d+)?$/.test(value);

const escapeCsvValue = (value) => {
  let text = String(value ?? "");
  const lead = text.replace(/^[\t\r ]+/, "");

  if (/^[=+\-@]/.test(lead) && !isPlainNumber(lead)) {
    text = `'${text}`;
  }

  if (/[;"\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
};

const DataGridExport = ({
  tableData = [],
  columns = [],
  fetchRows,
  fileName = "export",
  onError,
  variant = "icons",
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const filteredColumns = (columns || []).filter(
    (column) =>
      column.field &&
      column.field !== "undefined" &&
      column.field !== "fileId" &&
      column.field !== "actions" &&
      column.disableExport !== true
  );

  const resolveRows = async () => {
    if (fetchRows) {
      return fetchRows();
    }

    return tableData;
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const rows = await resolveRows();
      const csvData = [
        filteredColumns.map((column) => column.headerName || column.field),
        ...rows.map((row) => filteredColumns.map((column) => escapeCsvValue(getCellValue(row, column)))),
      ];

      const blob = new Blob([csvData.map((row) => row.join(";")).join("\n")], {
        type: "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.csv`;
      a.setAttribute("aria-hidden", "true");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Unable to export CSV:", error);
      onError?.(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportXLSX = async () => {
    setIsExporting(true);
    try {
      const rows = await resolveRows();
      const xlsxData = rows.map((row) =>
        filteredColumns.reduce((acc, column) => {
          acc[column.headerName || column.field] = getCellValue(row, column);
          return acc;
        }, {})
      );

      await downloadXlsx(xlsxData, "Sheet1", `${fileName}.xlsx`);
    } catch (error) {
      console.error("Unable to export XLSX:", error);
      onError?.(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenMenu = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleMenuExportCSV = async () => {
    handleCloseMenu();
    await handleExportCSV();
  };

  const handleMenuExportXLSX = async () => {
    handleCloseMenu();
    await handleExportXLSX();
  };

  if (variant === "toolbar") {
    return (
      <>
        <Button
          size="small"
          startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />}
          onClick={handleOpenMenu}
          disabled={isExporting}
          aria-label="Export"
        >
          Export
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <MenuItem onClick={handleMenuExportCSV} disabled={isExporting}>
            Download as CSV
          </MenuItem>
          <MenuItem onClick={handleMenuExportXLSX} disabled={isExporting}>
            Download as Excel
          </MenuItem>
        </Menu>
      </>
    );
  }

  return (
    <>
      <Tooltip title={isExporting ? "Preparing export…" : "Export to CSV"}>
        <span>
          <IconButton
            color="primary"
            onClick={handleExportCSV}
            disabled={isExporting}
            aria-label="Export to CSV"
          >
            {isExporting ? <CircularProgress size={18} /> : <CsvIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={isExporting ? "Preparing export…" : "Export to XLSX"}>
        <span>
          <IconButton
            color="primary"
            onClick={handleExportXLSX}
            disabled={isExporting}
            aria-label="Export to XLSX"
          >
            {isExporting ? <CircularProgress size={18} /> : <XlsxIcon />}
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};

export default DataGridExport;
